import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV, BRAND } from "@/data/brand";
import PshlWordmark from "@/components/PshlWordmark";

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="pshl-header"
      className="fixed top-0 inset-x-0 z-50 border-b border-white/10 backdrop-blur-xl bg-[#020A1A]/80"
    >
      <div className="pshl-container flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center shrink-0"
          onClick={() => setOpen(false)}
        >
          <img
            src="/pshl8-header-mark.png"
            alt="PSHL∞ — Pro Skate Hockey League"
            draggable={false}
            className="h-9 md:h-11 w-auto select-none"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-5 ml-8 mr-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.16em] font-semibold transition-colors ${
                  isActive ? "text-pshl-electric" : "text-white/80 hover:text-white"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:block shrink-0 ml-auto">
          <Link
            to="/contact"
            data-testid="header-cta-register"
            className="inline-flex items-center gap-2 bg-pshl-electric text-white font-display uppercase tracking-[0.18em] text-xs px-5 py-3 hover:bg-pshl-electric/90 hover:shadow-[0_0_24px_rgba(0,85,255,0.45)] transition-all whitespace-nowrap"
          >
            Register Interest <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <button
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden p-2 text-white"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="xl:hidden fixed inset-0 top-16 bg-[#020A1A]/98 backdrop-blur-xl overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="pshl-container py-8 grid gap-1">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <NavLink
                    to={n.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `flex items-center justify-between py-4 border-b border-white/10 font-display uppercase tracking-[0.18em] text-lg ${
                        isActive ? "text-pshl-electric" : "text-white"
                      }`
                    }
                    end={n.to === "/"}
                  >
                    <span>{n.label}</span>
                    <ChevronRight className="h-4 w-4 opacity-60" />
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-register"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-pshl-electric text-white font-display uppercase tracking-[0.18em] text-sm px-6 py-4 hover:bg-pshl-electric/90"
              >
                Register Interest <ChevronRight className="h-4 w-4" />
              </Link>
              <a
                href={BRAND.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 mt-3 border border-white/15 text-white font-display uppercase tracking-[0.18em] text-sm px-6 py-4 hover:border-white"
              >
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
